/**
 * ClaimShield driver and mechanic portal — entry point.
 *
 * Serves: drivers.claimshieldpro.com and mechanics.claimshieldpro.com
 * Compatibility: rewards.claimshieldpro.com during the host transition.
 *
 * Auth path: magic-link email → audience-bound JWT (aud="driver-portal").
 * Separate from staff portals; no shared session with portal/ops.
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import type { RouteLocationNormalized } from 'vue-router'

import PrimeVue from 'primevue/config'
import Aura from '@primevue/themes/aura'
import { definePreset } from '@primevue/themes'
import ToastService from 'primevue/toastservice'

// Override Aura's default emerald-green primary palette with ClaimShield purple
// so PrimeVue components (Button, focus rings, links, etc.) match the brand.
// Values mirror tailwind.config.js → colors.primary so Tailwind utilities
// (bg-primary-700, text-primary-800) and PrimeVue tokens agree.
//
// The ramp alone is not enough (learned 2026-08-11, on ops). Aura resolves
// `primary.color` to `{primary.500}`, but the brand anchor is #3d2683 = 700 on
// our scale, so a ramp-only preset ships purple-500 (#7952b3) as the action
// colour and leaves the anchor unused — visible on focus rings, checkboxes,
// toggles, radio fills, tab underlines and links. The colorScheme pin lands
// them on the anchor. Same block as packages/ops/src/main.ts; keep the four in
// sync (ops, portal, driver-portal, marketing). `.dark-mode` is never applied
// in this app, so the light scheme is the only one that renders.
const ClaimShieldAura = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f5f3fa',
      100: '#ebe6f5',
      200: '#d4c9eb',
      300: '#b8a3dc',
      400: '#9575c9',
      500: '#7952b3',
      600: '#6b4faf',
      700: '#3d2683',
      800: '#2a1a5e',
      900: '#1c1240',
      950: '#0e0920',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.700}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.800}',
          activeColor: '{primary.900}',
        },
      },
    },
  },
})

import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Toast from 'primevue/toast'
import ProgressSpinner from 'primevue/progressspinner'
import Card from 'primevue/card'

import { posthogPlugin } from '@claimshield/shared/plugins/posthog'

import App from './App.vue'
import { router } from './router'
import { currentSurface } from './config/hosts'

import './assets/main.css'
import 'primeicons/primeicons.css'

const app = createApp(App)

app.use(createPinia())

app.use(PrimeVue, {
  theme: {
    preset: ClaimShieldAura,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-mode',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
  ripple: true,
})

app.use(ToastService)

app.component('PButton', Button)
app.component('PInputText', InputText)
app.component('PToast', Toast)
app.component('PProgressSpinner', ProgressSpinner)
app.component('PCard', Card)

app.use(router)

// Product analytics — Phase 2 (knowledge/product-analytics/TRACK.md §5).
// Inert unless window.location.hostname is on the allowlist in
// @claimshield/shared/plugins/posthog.ts. Reaches PostHog through the
// same-origin /ingest/* Pages Function beside the /api/* one this app already
// runs, never directly.
app.use(posthogPlugin, {
  router,
  app: currentSurface() === 'mechanic' ? 'mechanic' : 'driver',
  appForRoute: () => currentSurface() === 'mechanic' ? 'mechanic' : 'driver',
  propertiesForRoute: (route: RouteLocationNormalized) => route.path.startsWith('/rewards') ? { section: 'rewards' } : {},
  redact: [
    { pattern: /\/auth\/verify\/[^/?#\s]+/g, replacement: '/auth/verify/:token' },
    { pattern: /\/garage\/annual\/[^/?#\s]+/g, replacement: '/garage/annual/:token' },
    { pattern: /%2[fF]auth%2[fF]verify%2[fF][^&#?\s]+/g, replacement: '%2Fauth%2Fverify%2F:token' },
    { pattern: /%2[fF]garage%2[fF]annual%2[fF][^&#?\s]+/g, replacement: '%2Fgarage%2Fannual%2F:token' },
  ],
})

app.mount('#app')
